import React, { useRef, useEffect, useState, useCallback } from 'react';
import usePlayerStore from '../store/playerStore';
import FullScreenPlayer from './FullScreenPlayer';
import PlaylistPicker from './PlaylistPicker';

function formatTime(s) {
  if (!s || isNaN(s)) return '0:00';
  const m = Math.floor(s / 60);
  return `${m}:${String(Math.floor(s % 60)).padStart(2, '0')}`;
}

export default function Player() {
  const audioRef = useRef(null);
  const currentTrackIdRef = useRef(null);
  const {
    currentTrack, isPlaying, volume, shuffle, repeat,
    setAudioRef, togglePlay, next, prev, onTrackEnd,
    setVolume, toggleShuffle, toggleRepeat, getStreamUrl,
  } = usePlayerStore();

  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [fullScreen, setFullScreen] = useState(false);
  const [pickerPos, setPickerPos] = useState(null); // null = closed; {x,y} or {} = open
  const wakeLockRef = useRef(null);

  // Auto-open full screen on mobile only when starting fresh playback (not on next/prev)
  const prevTrackIdRef = useRef(null);
  useEffect(() => {
    if (currentTrack && window.matchMedia('(max-width: 768px)').matches) {
      if (!prevTrackIdRef.current) {
        // Was no track before — this is a fresh play, open fullscreen
        setFullScreen(true);
      }
      // Track changed within existing playback — leave fullscreen state alone
    }
    prevTrackIdRef.current = currentTrack?.id ?? null;
  }, [currentTrack?.id]);

  useEffect(() => {
    setAudioRef(audioRef);
    if (audioRef.current) audioRef.current.volume = volume;
  }, [setAudioRef]); // intentionally omitting volume from deps — only set once on mount

  // Spacebar play/pause shortcut
  useEffect(() => {
    const handler = (e) => {
      if (e.code !== 'Space') return;
      const tag = e.target.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || e.target.isContentEditable) return;
      e.preventDefault();
      togglePlay();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [togglePlay]);

  // Helper: push current track info to Media Session API
  const updateMediaSession = useCallback((track) => {
    if (!('mediaSession' in navigator) || !track) return;

    const rawUrl = track.cover_art_url;
    const artUrl = rawUrl
      ? (rawUrl.startsWith('http') ? rawUrl : window.location.origin + rawUrl)
      : null;
    const mimeType = artUrl && artUrl.match(/\.png(\?|$)/i) ? 'image/png' : 'image/jpeg';

    console.log('[MediaSession] setting metadata:', track.title, track.artist_name, track.album_title, artUrl);

    navigator.mediaSession.metadata = new MediaMetadata({
      title: track.title || '',
      artist: track.artist_name || '',
      album: track.album_title || '',
      artwork: artUrl
        ? [
            { src: artUrl, sizes: '96x96',  type: mimeType },
            { src: artUrl, sizes: '512x512', type: mimeType },
          ]
        : [],
    });

    navigator.mediaSession.setActionHandler('play',          () => togglePlay());
    navigator.mediaSession.setActionHandler('pause',         () => togglePlay());
    navigator.mediaSession.setActionHandler('previoustrack', () => prev());
    navigator.mediaSession.setActionHandler('nexttrack',     () => next());
  }, [togglePlay, prev, next]);

  // Handle track changes
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !currentTrack) return;

    currentTrackIdRef.current = currentTrack.id;
    const thisTrackId = currentTrack.id;

    audio.src = getStreamUrl(currentTrack);
    audio.load();

    if (isPlaying) audio.play().catch(() => {});

    // Set metadata immediately and again on play (iOS needs it on the play event)
    updateMediaSession(currentTrack);
    const handlePlay = () => updateMediaSession(currentTrack);
    const handleEnded = () => {
      if (currentTrackIdRef.current === thisTrackId) onTrackEnd();
    };

    audio.addEventListener('play', handlePlay);
    audio.addEventListener('ended', handleEnded);
    return () => {
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('ended', handleEnded);
    };
  }, [currentTrack]);

  // Handle play/pause toggling (not track changes)
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    isPlaying ? audio.play().catch(() => {}) : audio.pause();
    if ('mediaSession' in navigator) {
      navigator.mediaSession.playbackState = isPlaying ? 'playing' : 'paused';
    }
  }, [isPlaying]);

  // Handle keeping the screensaver from coming on while music is playing
  useEffect(() => {
    const requestWakeLock = async () => {
      try {
        if (isPlaying && 'wakeLock' in navigator) {
          wakeLockRef.current = await navigator.wakeLock.request('screen');
        } else if (!isPlaying && wakeLockRef.current) {
          await wakeLockRef.current.release();
          wakeLockRef.current = null;
        }
      } catch (e) {
        // Wake lock can fail if tab is not visible, ignore silently
      }
    };
    requestWakeLock();
  }, [isPlaying]);

  const handleTimeUpdate = () => {
    const a = audioRef.current;
    if (a) { setProgress(a.currentTime); setDuration(a.duration || 0); }
  };

  const handleSeek = (e) => {
    const a = audioRef.current;
    if (a && duration) { a.currentTime = (e.target.value / 100) * duration; }
  };

  const repeatIcon = { none: '↻', one: '🔂', all: '🔁' };

  if (!currentTrack) {
    return (
      <div className="player-bar">
        <audio ref={audioRef} />
        <span style={{ color: 'var(--text-muted)', margin: '0 auto' }}>No track selected</span>
      </div>
    );
  }

  return (
    <div className="player-bar">
      <audio
        ref={audioRef}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleTimeUpdate}
        volume={volume}
      />

      {/* Left — Track info, fixed width */}
      <div className="player-track-info">
        {currentTrack.cover_art_url
          ? <img className="player-album-art" src={currentTrack.cover_art_url} alt="" onClick={() => setFullScreen(true)} style={{ cursor: 'pointer' }} />
          : <div className="player-album-art" onClick={() => setFullScreen(true)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, cursor: 'pointer' }}>🎵</div>
        }
        <div style={{ overflow: 'hidden', flex: 1, minWidth: 0 }}>
          <div className="player-track-name" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {currentTrack.title}
          </div>
          <div className="player-track-artist" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {currentTrack.artist_name}
          </div>
        </div>
        <button
          onClick={(e) => {
            e.stopPropagation();
            if (window.innerWidth > 768) {
              const rect = e.currentTarget.getBoundingClientRect();
              setPickerPos({ x: rect.left, y: rect.bottom + 4 });
            } else {
              setPickerPos({});  // no position = bottom sheet
            }
          }}
          title="Add to playlist"
          style={{
            flexShrink: 0,
            background: 'none',
            border: '1px solid var(--border)',
            color: 'var(--text-muted)',
            borderRadius: 6,
            padding: '4px 9px',
            fontSize: 16,
            cursor: 'pointer',
            lineHeight: 1,
          }}
        >
          ＋
        </button>
      </div>

      {/* Center — Controls, absolutely centered */}
      <div className="player-controls">
        <div className="player-buttons">
          <button className="player-btn" onClick={prev} title="Previous">⏮</button>
          <button className="player-btn player-play-btn" onClick={togglePlay}>{isPlaying ? '⏸' : '▶'}</button>
          <button className="player-btn" onClick={next} title="Next">⏭</button>
        </div>
        <div className="player-progress">
          <span className="player-time">{formatTime(progress)}</span>
          <input
            type="range" className="progress-bar" min="0" max="100"
            value={duration ? (progress / duration) * 100 : 0}
            onChange={handleSeek}
          />
          <span className="player-time right">{formatTime(duration)}</span>
        </div>
      </div>

      {/* Right — Volume + Full Screen button, fixed width */}
      <div className="player-right">
        <span>🔊</span>
        <input
          type="range" className="volume-slider" min="0" max="1" step="0.02"
          value={volume}
          onChange={(e) => setVolume(parseFloat(e.target.value))}
        />
        <button
          onClick={() => setFullScreen(true)}
          style={{
            marginLeft: 16,
            background: 'rgba(255,255,255,0.1)',
            border: '1px solid var(--border)',
            color: 'var(--text)',
            borderRadius: 6,
            padding: '5px 12px',
            fontSize: 12,
            cursor: 'pointer',
            whiteSpace: 'nowrap',
          }}
        >
          ⛶ Full Screen
        </button>
      </div>

      {/* Full screen overlay */}
      {fullScreen && (
        <FullScreenPlayer
          progress={progress}
          duration={duration}
          onSeek={handleSeek}
          onExit={() => setFullScreen(false)}
        />
      )}

      {/* Add to playlist picker */}
      {pickerPos !== null && currentTrack && (
        <PlaylistPicker
          track={currentTrack}
          position={pickerPos.x !== undefined ? pickerPos : undefined}
          onClose={() => setPickerPos(null)}
        />
      )}
    </div>
  );
}
